import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { BusinessAccount } from "@/api.types";
import { useSession } from "@/services/auth0/session";

export interface BusinessAccountFormData {
  companyName: string;
  taxNumber?: string;
  registrationNo?: string;
}

export function useCreateBusinessAccount({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: AxiosError<{ message: string }>) => void;
}) {
  const queryClient = useQueryClient();
  const { token, customer } = useSession();

  return useMutation({
    mutationFn: async (data: BusinessAccountFormData) => {
      const response = await axios.post<BusinessAccount>(
        `${import.meta.env.VITE_API_URL}/api/v1/business-account`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["customer", customer?.id, token],
      });
      onSuccess?.();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      onError?.(error);
    },
  });
}

export function useUpdateBusinessAccount({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: AxiosError<{ message: string }>) => void;
}) {
  const queryClient = useQueryClient();
  const { token, customer } = useSession();

  return useMutation({
    mutationFn: async ({
      customerId,
      businessAccountId,
      data,
    }: {
      customerId: string;
      businessAccountId: string;
      data: BusinessAccountFormData;
    }) => {
      const response = await axios.patch<BusinessAccount>(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customerId}/business-accounts/${businessAccountId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["customer", customer?.id, token],
      });
      onSuccess?.();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      onError?.(error);
    },
  });
}
